import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnOrderState,
} from '@tanstack/react-table';
import { cn } from '../utils/cn';
import { DateFilterContent } from './filters/DateFilterContent';

export interface SpreadsheetPlainProps<T extends { id: string }> {
  // Data
  data: T[];
  columns: ColumnDef<T>[];
  
  // Callbacks
  onRowUpdate?: (rowId: string, field: keyof T, value: any) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onRowClick?: (row: T) => void;
  
  // Filtering
  enableFiltering?: boolean;
  filterableColumns?: string[];
  uniqueValues?: Record<string, string[]>;
  columnFilters?: Record<string, string[]>;
  onColumnFiltersChange?: (filters: Record<string, string[]>) => void;
  
  // Date filtering
  enableDateFiltering?: boolean;
  selectedYears?: string[];
  selectedMonths?: string[];
  onYearSelectionChange?: (years: string[]) => void;
  onMonthSelectionChange?: (months: string[]) => void;
  availableYears?: string[];
  availableMonths?: string[];
  
  // Sorting
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState | ((old: SortingState) => SortingState)) => void;
  
  // Column ordering
  enableColumnReordering?: boolean;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (order: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => void;
  
  // Selection
  enableSelection?: boolean;
  selectedRows?: Set<string>;
  
  // Infinite scroll
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isFetchingPosts?: boolean;
  onLoadMore?: () => void;
  
  // Styling
  className?: string;
  emptyMessage?: string;
  hideStatusBar?: boolean;
  hideLoadMoreIndicator?: boolean;
  rowHeight?: number;
  /** Opt into the Grid dark theme (recolors the fixed light Tailwind classes). */
  darkMode?: boolean;
}

const ROW_HEIGHT = 52;

export function SpreadsheetPlain<T extends { id: string }>({
  data,
  columns,
  onRowUpdate,
  onSelectionChange,
  onRowClick,
  enableFiltering = false,
  filterableColumns = [],
  uniqueValues = {},
  columnFilters = {},
  onColumnFiltersChange,
  enableDateFiltering = false,
  selectedYears = [],
  selectedMonths = [],
  onYearSelectionChange,
  onMonthSelectionChange,
  availableYears = [],
  availableMonths = [],
  manualSorting = false,
  sorting = [],
  onSortingChange,
  enableColumnReordering = false,
  columnOrder,
  onColumnOrderChange,
  enableSelection = false,
  selectedRows = new Set<string>(),
  hasNextPage = false,
  isFetchingNextPage = false,
  isFetchingPosts = false,
  onLoadMore,
  className = '',
  emptyMessage = 'No data available',
  hideStatusBar = false,
  hideLoadMoreIndicator = false,
  rowHeight = ROW_HEIGHT,
  darkMode = false,
}: SpreadsheetPlainProps<T>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>(sorting);
  const [internalColumnOrder, setInternalColumnOrder] = useState<ColumnOrderState>(columnOrder || []);
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const filterTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [filterPosition, setFilterPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const listOuterRef = useRef<HTMLDivElement | null>(null);

  // Callback to capture List's outer element - it will use parent scroll container
  const setListOuterRef = useCallback((element: HTMLDivElement | null) => {
    listOuterRef.current = element;
  }, []);

  // Sync internal sorting with prop when it changes
  useEffect(() => {
    if (onSortingChange) {
      setInternalSorting(sorting);
    }
  }, [sorting, onSortingChange]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-filter-dropdown]') && !target.closest('[data-filter-trigger]')) {
        setOpenFilterColumn(null);
        setFilterPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update filter position on scroll
  useEffect(() => {
    if (openFilterColumn && filterPosition) {
      const updatePosition = () => {
        const triggerElement = filterTriggerRefs.current[openFilterColumn];
        if (triggerElement) {
          const rect = triggerElement.getBoundingClientRect();
          setFilterPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [openFilterColumn, filterPosition]);


  const handleFilterClick = useCallback((columnId: string) => {
    if (!enableFiltering || !filterableColumns.includes(columnId)) return;
    const isOpening = openFilterColumn !== columnId;
    setOpenFilterColumn(prev => prev === columnId ? null : columnId);
    
    if (isOpening) {
      // Calculate position for the filter dropdown
      const triggerElement = filterTriggerRefs.current[columnId];
      if (triggerElement) {
        const rect = triggerElement.getBoundingClientRect();
        setFilterPosition({
          top: rect.bottom + 4, // 4px = mt-1 equivalent
          left: rect.left,
          width: rect.width,
        });
      }
    } else {
      setFilterPosition(null);
    }
  }, [enableFiltering, filterableColumns, openFilterColumn]);

  const handleFilterChange = useCallback((columnId: string, values: string[]) => {
    if (!onColumnFiltersChange) return;
    const newFilters = { ...columnFilters, [columnId]: values };
    if (values.length === 0) {
      delete newFilters[columnId];
    }
    onColumnFiltersChange(newFilters);
  }, [columnFilters, onColumnFiltersChange]);

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.id,
    state: {
      sorting: onSortingChange ? sorting : internalSorting,
      columnOrder: onColumnOrderChange ? columnOrder : internalColumnOrder,
    },
    onSortingChange: (updaterOrValue) => {
      if (onSortingChange) {
        // Handle both function updater and direct value
        if (typeof updaterOrValue === 'function') {
          onSortingChange(updaterOrValue(sorting));
        } else {
          onSortingChange(updaterOrValue);
        }
      } else {
        // Handle both function updater and direct value
        if (typeof updaterOrValue === 'function') {
          setInternalSorting(updaterOrValue);
        } else {
          setInternalSorting(updaterOrValue);
        }
      }
    },
    onColumnOrderChange: (updaterOrValue) => {
      if (onColumnOrderChange) {
        if (typeof updaterOrValue === 'function') {
          onColumnOrderChange(updaterOrValue(columnOrder || []));
        } else {
          onColumnOrderChange(updaterOrValue);
        }
      } else {
        if (typeof updaterOrValue === 'function') {
          setInternalColumnOrder(updaterOrValue);
        } else {
          setInternalColumnOrder(updaterOrValue);
        }
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
  });

  // Column reordering handlers (defined after table)
  const handleDragStart = useCallback((e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) return;

    // Get current column order from state (not from table.getAllColumns() which may not reflect display order)
    const currentOrder = onColumnOrderChange 
      ? (columnOrder || table.getAllColumns().map(col => col.id))
      : (internalColumnOrder.length > 0 
          ? internalColumnOrder 
          : table.getAllColumns().map(col => col.id));
    
    const draggedIndex = currentOrder.indexOf(draggedColumn);
    const targetIndex = currentOrder.indexOf(targetColumnId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    if (onColumnOrderChange) {
      onColumnOrderChange(newOrder);
    } else {
      setInternalColumnOrder(newOrder);
    }
  }, [draggedColumn, table, onColumnOrderChange, columnOrder, internalColumnOrder]);

  const handleDragEnd = useCallback(() => {
    setDraggedColumn(null);
  }, []);

  // Get current sorting state for dependency tracking
  const currentSorting = onSortingChange ? sorting : internalSorting;
  
  const rows = useMemo(() => {
    return table.getRowModel().rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, currentSorting, data.length]);
  
  // Get current column order for dependency tracking
  const currentColumnOrder = onColumnOrderChange ? columnOrder : internalColumnOrder;
  // Use JSON.stringify to ensure memo recomputes when array contents change
  const columnOrderKey = JSON.stringify(currentColumnOrder);
  const headers = useMemo(() => {
    const headerGroups = table.getHeaderGroups();
    return headerGroups[0]?.headers || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columnOrderKey]);

  // Calculate total width of all columns (for minimum width)

  // Render filter dropdown outside header container
  const filterDropdown = useMemo(() => {
    if (!openFilterColumn || !filterPosition) return null;
    
    const header = headers.find(h => h.column.id === openFilterColumn);
    if (!header) return null;
    const columnId = header.column.id;
    const isFilterable = enableFiltering && filterableColumns.includes(columnId);
    if (!isFilterable) return null;
    
    const selectedFilters = columnFilters[columnId] || [];
    const hasActiveFilters = selectedFilters.length > 0;
    const filterOptions = uniqueValues[columnId] || [];
    
    const filterContent = (
      <div
        className={cn(
          'fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] min-w-[280px] max-w-[320px] max-h-[400px] overflow-hidden',
          darkMode && 'grid-dark-spreadsheet grid-dark-spreadsheet-popover'
        )}
        style={{
          top: `${filterPosition.top}px`,
          left: `${filterPosition.left}px`,
        }}
        data-filter-dropdown
      >
        {columnId === 'date' && enableDateFiltering ? (
          <DateFilterContent
            title="Date"
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
            onYearSelectionChange={onYearSelectionChange || (() => {})}
            onMonthSelectionChange={onMonthSelectionChange || (() => {})}
            availableYears={availableYears}
            availableMonths={availableMonths}
            onClose={() => {
              setOpenFilterColumn(null);
              setFilterPosition(null);
            }}
          />
        ) : (
          <>
            <div className="p-3 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold">Filter {flexRender(header.column.columnDef.header, header.getContext())}</span>
              {hasActiveFilters && (
                <button
                  className="text-xs text-blue-600 hover:text-blue-800"
                  onClick={() => handleFilterChange(columnId, [])}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-[240px] overflow-y-auto">
              {filterOptions.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">No options available</div>
              ) : (
                <div className="p-2">
                  {filterOptions.map((option) => {
                    const isChecked = selectedFilters.includes(option);
                    return (
                      <label
                        key={option}
                        className="flex items-center px-2 py-1.5 hover:bg-gray-50 cursor-pointer rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const newValues = isChecked
                              ? selectedFilters.filter(v => v !== option)
                              : [...selectedFilters, option];
                            handleFilterChange(columnId, newValues);
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700 flex-1 truncate" title={option}>
                          {option || '(Empty)'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {filterOptions.length > 0 && (
              <div className="p-2 border-t border-gray-200 text-xs text-gray-500">
                {selectedFilters.length} of {filterOptions.length} selected
              </div>
            )}
          </>
        )}
      </div>
    );
    
    return createPortal(filterContent, document.body);
  }, [openFilterColumn, filterPosition, headers, enableFiltering, filterableColumns, columnFilters, uniqueValues, enableDateFiltering, selectedYears, selectedMonths, onYearSelectionChange, onMonthSelectionChange, availableYears, availableMonths, handleFilterChange, darkMode]);

  // Track the last visible index to prevent duplicate requests
  const lastTriggeredIndexRef = useRef(-1);
  
  // Load more when near the end of the list
  const handleItemsRendered = useCallback(({
    visibleStartIndex,
    visibleStopIndex,
  }: {
    visibleStartIndex: number;
    visibleStopIndex: number;
  }) => {
    // Load more when we're within 100 rows of the end (load earlier to pre-render)
    const threshold = 100;
    const isNearEnd = visibleStopIndex >= rows.length - threshold;
    
    // Only trigger if we haven't already triggered for this scroll position
    if (isNearEnd && hasNextPage && !isFetchingNextPage && !isFetchingPosts && onLoadMore && lastTriggeredIndexRef.current !== visibleStopIndex) {
      lastTriggeredIndexRef.current = visibleStopIndex;
      onLoadMore();
    }
  }, [rows.length, hasNextPage, isFetchingNextPage, isFetchingPosts, onLoadMore]);

  // Reset the last triggered index when new data arrives (rows.length changes)
  useEffect(() => {
    lastTriggeredIndexRef.current = -1;
  }, [rows.length]);

  // Virtualized row renderer
  const Row = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const row = rows[index];
    if (!row) return null;
    
    const isSelected = enableSelection && selectedRows.has(row.original.id);

    return (
      <div
        style={{ ...style, width: '100%', maxWidth: '100%' }}
        className={cn(
          'flex items-center border-b border-gray-200 hover:bg-gray-50 transition-colors',
          isSelected && 'bg-blue-50 border-l-4 border-l-blue-500',
          (onRowClick || (enableSelection && onSelectionChange)) && 'cursor-pointer'
        )}
        onClick={() => {
          if (enableSelection && onSelectionChange) {
            const newSelection = new Set(selectedRows);
            if (newSelection.has(row.original.id)) {
              newSelection.delete(row.original.id);
            } else {
              newSelection.add(row.original.id);
            }
            onSelectionChange(newSelection);
          } else if (onRowClick) {
            onRowClick(row.original);
          }
        }}
      >
        {row.getVisibleCells().map((cell, cellIndex) => {
          const column = cell.column;
          const columnId = column.id;
          const width = column.getSize();
          const minWidth = column.columnDef.minSize || width;
          const maxWidth = column.columnDef.maxSize || width;
          // Make text column flexible to fill remaining space
          const isFlexible = columnId === 'text';
          
          // Check if this column is draggable to match header structure
          const isDraggableCell = enableColumnReordering && columnId !== 'select';
          // Check if column is sortable to match header structure (for alignment)
          const canSortCell = column.getCanSort();
          const isFirstColumn = cellIndex === 0;
          
          return (
            <div
              key={cell.id}
              className={cn(
                'flex items-center',
                isFirstColumn ? 'border-l border-l-gray-300' : '',
                'border-r border-r-gray-300'
              )}
              style={{
                width: isFlexible ? undefined : `${width}px`,
                minWidth: `${minWidth}px`,
                maxWidth: isFlexible ? undefined : `${maxWidth}px`,
                flex: isFlexible ? '1 1 0' : '0 0 auto',
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                boxSizing: 'content-box',
              }}
            >
              {isDraggableCell && (
                <span className="text-gray-400 mr-1 invisible flex-shrink-0" aria-hidden="true">
                  ⋮⋮
                </span>
              )}
              <div 
                className={columnId === 'media' ? "flex-1 w-full" : "flex-1 min-w-0 w-full"} 
                style={{ 
                  width: '100%',
                  maxWidth: columnId === 'media' ? 'none' : '100%',
                  overflow: (columnId === 'promotePercentage' || columnId === 'media') ? 'visible' : 'hidden',
                }}
              >
                <div 
                  className="w-full" 
                  style={{ 
                    width: '100%', 
                    minWidth: columnId === 'media' ? 'auto' : 0,
                    maxWidth: columnId === 'media' ? 'none' : '100%',
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </div>
              {canSortCell && (
                <span className="text-gray-400 invisible ml-2 flex-shrink-0" aria-hidden="true">
                  ⇅
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [rows, enableSelection, selectedRows, onSelectionChange, onRowClick, enableColumnReordering]);

  const handleSort = useCallback((columnId: string) => {
    const column = table.getColumn(columnId);
    if (!column || !column.getCanSort()) {
      return;
    }
    
    const currentSorting = onSortingChange ? sorting : internalSorting;
    const currentSort = currentSorting.find(s => s.id === columnId);
    let newSort: SortingState;
    
    if (!currentSort) {
      newSort = [{ id: columnId, desc: false }];
    } else if (!currentSort.desc) {
      newSort = [{ id: columnId, desc: true }];
    } else {
      newSort = [];
    }
    
    if (onSortingChange) {
      onSortingChange(newSort);
    } else {
      setInternalSorting(newSort);
    }
  }, [table, onSortingChange, sorting, internalSorting]);

  if (data.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12', className)}>
        <div className="text-gray-400 text-6xl mb-4">📊</div>
        <p className="text-gray-600 text-lg font-medium">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div 
      className={cn('flex flex-col h-full bg-white border border-gray-200 rounded-lg overflow-hidden', darkMode && 'grid-dark-spreadsheet', className)}
      style={{ width: '100%', minWidth: '100%' }}
    >
      {/* Scroll container wraps both header and body - they both react to this scroll */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-auto relative"
        style={{ overflowX: 'auto', overflowY: 'auto' }}
      >
        {/* Sticky Header - stays at top, scrolls horizontally with scroll container */}
        <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
          <div 
            className="flex items-center relative" 
            style={{ width: '100%', maxWidth: '100%' }}
          >
          {headers.map((header, headerIndex) => {
            const column = header.column;
            const columnId = column.id;
            const canSort = column.getCanSort();
            const isFilterable = enableFiltering && filterableColumns.includes(columnId);
            const sortDirection = column.getIsSorted();
            const width = column.getSize();
            const minWidth = column.columnDef.minSize || width;
            const maxWidth = column.columnDef.maxSize || width;
            const selectedFilters = columnFilters[columnId] || [];
            const hasActiveFilters = selectedFilters.length > 0;
            
            const isDraggable = enableColumnReordering && columnId !== 'select';
            // Make text column flexible to fill remaining space
            const isFlexible = columnId === 'text';
            const isFirstColumn = headerIndex === 0;
            
            return (
              <div
                key={header.id}
                className={cn(
                  'relative font-semibold text-xs uppercase tracking-wider text-gray-700',
                  'flex items-center',
                  isFirstColumn ? 'border-l border-l-gray-300' : '',
                  'border-r border-r-gray-300',
                  hasActiveFilters && 'bg-blue-50',
                  isDraggable && draggedColumn === columnId && 'opacity-50',
                  isDraggable && 'cursor-move'
                )}
                style={{
                  width: isFlexible ? undefined : `${width}px`,
                  minWidth: `${minWidth}px`,
                  maxWidth: isFlexible ? undefined : `${maxWidth}px`,
                  flex: isFlexible ? '1 1 0' : '0 0 auto',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  boxSizing: 'content-box',
                }}
                draggable={isDraggable}
                onDragStart={isDraggable ? (e) => handleDragStart(e, columnId) : undefined}
                onDragOver={isDraggable ? handleDragOver : undefined}
                onDrop={isDraggable ? (e) => handleDrop(e, columnId) : undefined}
                onDragEnd={isDraggable ? handleDragEnd : undefined}
              >
                {isDraggable && (
                  <span className="text-gray-400 mr-1 flex-shrink-0" title="Drag to reorder">
                    ⋮⋮
                  </span>
                )}
                <div
                  ref={(el) => {
                    if (isFilterable) {
                      filterTriggerRefs.current[columnId] = el;
                    }
                  }}
                  className={cn(
                    'flex-1 overflow-hidden text-ellipsis whitespace-nowrap flex items-center gap-1',
                    isFilterable && 'cursor-pointer hover:bg-gray-100 select-none px-1 -mx-1 rounded'
                  )}
                  onClick={(e) => {
                    if (isFilterable) {
                      e.stopPropagation();
                      handleFilterClick(columnId);
                    }
                  }}
                  data-filter-trigger={isFilterable}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {hasActiveFilters && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-500 rounded-full">
                      {selectedFilters.length}
                    </span>
                  )}
                </div>
                {canSort && (
                  <span
                    className="text-gray-400 hover:text-gray-600 cursor-pointer ml-2 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSort(columnId);
                    }}
                  >
                    {sortDirection === 'asc' && '↑'}
                    {sortDirection === 'desc' && '↓'}
                    {!sortDirection && '⇅'}
                  </span>
                )}
              </div>
            );
          })}
          </div>
        </div>

        {/* Filter Dropdown - Rendered outside header container */}
        {filterDropdown}

        {/* Virtualized Body - uses parent scroll container natively via outerRef */}
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <List
              height={height}
              itemCount={rows.length}
              itemSize={rowHeight}
              width={width}
              overscanCount={50} // Pre-render 50 rows outside viewport for smoother scrolling
              onItemsRendered={handleItemsRendered}
              outerRef={setListOuterRef}
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      </div>

      {/* Load More Indicator */}
      {hasNextPage && !hideLoadMoreIndicator && (
        <div className="flex items-center justify-center border-t border-gray-200 py-2">
          {isFetchingNextPage || isFetchingPosts ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              <span>Loading more...</span>
            </div>
          ) : (
            <span className="text-gray-500 text-sm">Scroll to load more...</span>
          )}
        </div>
      )}

      {/* Status Bar */}
      {!hideStatusBar && data.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
          <span>
            Showing {data.length} {data.length === 1 ? 'row' : 'rows'}
            {hasNextPage && ' (scroll for more)'}
          </span>
          {enableSelection && selectedRows.size > 0 && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
              {selectedRows.size} selected
            </span>
          )}
        </div>
      )}
    </div>
  );
}
